import React, { useCallback, useEffect } from "react";
import { useLocation, useHistory } from "react-router";
import { connect, useDispatch, useSelector, useStore } from "react-redux";
import { AIInsightsClient as AIBIApp } from "@yoast/ai-insights-client";
import { injectIntl } from "react-intl";
import { getAccessToken } from "shared-frontend/functions/auth";
import { logout } from "shared-frontend/redux/actions/user";
import { retrieveSites } from "shared-frontend/redux/actions/sites";
import getEnv from "shared-frontend/functions/getEnv";
import PropTypes from "prop-types";
import { getShopifyShopHostname } from "../../redux/selectors/ui/sites";

import DebugInfo from "../DebugInfo";
import { aiBrandInsightsPath } from "../../config/Menu";
import { useEnvironment } from "../../reactHooks/useEnvironment";

const apiUrl = getEnv( "AIBI_API_URL", "http://127.0.0.1:8000/api/v1" );

/**
 * The Brand Insights Tool component.
 *
 * @param {Object} props The component props.
 * @param {Function} props.onLogout The logout handler.
 * @returns {ReactElement} The Brand Insights Tool component.
 */
const BrandInsightsTool = ( { onLogout } ) => {
	const location = useLocation();
	const history = useHistory();
	const { env } = useEnvironment();
	const dispatch = useDispatch();
	const store = useStore();
	const shopifyShopHostname = useSelector( getShopifyShopHostname );
	const sitesRetrieved = useSelector( ( state ) => state.ui.sites.sitesRetrieved );

	useEffect( () => {
		if ( ! sitesRetrieved ) {
			dispatch( retrieveSites() );
		}
	}, [ dispatch, sitesRetrieved ] );

	// Note: previously named `AIInsightsClient`
	// https://github.com/yoast/ai-insights-frontend
	return (
		<AIBIApp
			apiUrl={ apiUrl }
			DebugInfo={ DebugInfo }
			token={ useCallback( () => getAccessToken(), [] ) }
			basename={ aiBrandInsightsPath || "/" }
			subpath={ location.pathname.replace( aiBrandInsightsPath, "" ) || "/" }
			onNavigate={ ( to ) => history.push( `${aiBrandInsightsPath}${to}` ) }
			onLogout={ onLogout }
			env={ env }
			// https://github.com/Yoast/Platform/issues/3326
			// NOTE: this link may change to the product page once it exists (possibly at `/product/ai-brand-insight/`)
			// currently this will link directly to the cart, adding the insights product
			// NOTE: if we change this to a my-yoast link, use history.push instead of window.location.href
			onAddSubscription={ async() => {
				let hostname = shopifyShopHostname;
				if ( ! sitesRetrieved ) {
					await dispatch( retrieveSites() );
					hostname = getShopifyShopHostname( store.getState() );
				}
				window.location.href = hostname
					? `https://${ hostname }/admin/apps/yoast-seo-2/account`
					: `${getEnv( "SHOP_URL", "https://yoast.test" )}/cart/?add-to-cart=4184981`;
			} }
		/>
	);
};

BrandInsightsTool.propTypes = {
	onLogout: PropTypes.func.isRequired,
};

/**
 * Maps dispatch to props.
 *
 * @param {Function} dispatch The redux dispatch function.
 * @returns {Object} The mapped props.
 */
const mapDispatchToProps = ( dispatch ) => ( {
	onLogout: () => dispatch( logout() ),
} );

export default connect( null, mapDispatchToProps )( injectIntl( BrandInsightsTool ) );
